import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import MeridianLockup from "../components/MeridianLockup";
import { signInWithEmail, useAuth } from "../lib/auth";

interface LocationState {
  from?: string;
}

/**
 * Email + password login page. Phase 5 minimal surface. Supabase Auth
 * handles email confirmation + reset flows out-of-band; MFA enroll
 * lives on Supabase's hosted page until Phase 6 cutover when AJ owns
 * the TOTP toggle.
 *
 * MER-168: brand lockup + tagline + forgot-password link + MFA-aware
 * copy. The MFA sub-line is a static notice today; Settings v1 in
 * MER-164 will gate it on per-user enrollment state.
 */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session) {
    // MER-515: already signed in (e.g. visiting /login via bookmark). A
    // captured deep link wins; otherwise route through "/", which the
    // RootResolver resolves to the user's dashboard (never the placeholder).
    const state = location.state as LocationState | null;
    return <Navigate to={state?.from ?? "/"} replace />;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email.trim(), password);
      const state = location.state as LocationState | null;
      // MER-515: a captured deep link wins; otherwise route to "/". "/" is no
      // longer the placeholder -- RootResolver resolves an authed user there
      // to their dashboard (staff -> /admin, member -> /<slug>). The
      // staff/slug decision is centralized in RootResolver on purpose: a
      // Login-side async resolve here would be dead code, because
      // signInWithPassword fires SIGNED_IN before it returns, so useAuth
      // re-renders and the render-time redirect above fires before any awaited
      // lookup -- and it would issue a second, redundant tenant_members query.
      navigate(state?.from ?? "/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "32px",
        background:
          "radial-gradient(at 30% 20%, rgba(38,168,159,0.06), transparent 50%), var(--bg)",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 16,
          padding: "36px 36px 28px",
          width: "100%",
          maxWidth: 380,
          boxShadow: "var(--shadow-card)",
          display: "grid",
          gap: 14,
        }}
      >
        <div style={{ display: "grid", justifyItems: "center", gap: 16, marginBottom: 4 }}>
          <MeridianLockup size="lg" align="stack" />
          <h1 style={{ fontSize: 18, fontWeight: 600, color: "var(--text)" }}>Sign in</h1>
        </div>

        <label style={{ display: "grid", gap: 6, fontSize: 13 }}>
          <span style={{ color: "var(--text-muted)" }}>Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            data-testid="login-email"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            style={inputStyle}
          />
        </label>

        <label style={{ display: "grid", gap: 6, fontSize: 13 }}>
          <span style={{ color: "var(--text-muted)" }}>Password</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            data-testid="login-password"
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            style={inputStyle}
          />
        </label>

        <div style={{ textAlign: "right", marginTop: -6 }}>
          <Link
            to="/forgot-password"
            style={{
              fontSize: 12,
              color: "var(--brand-deep)",
              textDecoration: "none",
            }}
          >
            Forgot password?
          </Link>
        </div>

        {error ? (
          <div
            role="alert"
            style={{
              fontSize: 13,
              color: "var(--flag-red)",
              background: "rgba(192, 57, 43, 0.08)",
              padding: "10px 12px",
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit"
          style={primaryButtonStyle}
        >
          {submitting ? "Signing in..." : "Sign in"}
        </button>

        <p
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            textAlign: "center",
            marginTop: -4,
          }}
        >
          If MFA is enabled on your account, you&apos;ll be asked for your authenticator code next.
        </p>

        <div
          style={{
            borderTop: "1px solid var(--border-subtle)",
            marginTop: 8,
            paddingTop: 12,
          }}
        >
          <p style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
            Need access? Contact your Meridian account manager.
          </p>
        </div>
      </form>
    </main>
  );
}

const inputStyle: React.CSSProperties = {
  font: "inherit",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--border-subtle)",
  background: "var(--bg-elevated)",
  color: "var(--text-primary)",
};

const primaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "12px 14px",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(95deg, var(--brand-deep) 0%, var(--brand-teal) 100%)",
  color: "white",
  cursor: "pointer",
  letterSpacing: 0.3,
};
